import { useCallback, useEffect, useRef, useState } from 'react'

function toggleValueInSet(previous: Set<string>, value: string) {
    const next = new Set(previous)

    if (next.has(value)) {
        next.delete(value)
        return next
    }

    next.add(value)
    return next
}

export function useTaskpaneUiState() {
    const officeSelectRef = useRef<HTMLDivElement | null>(null)
    const filterPanelRef = useRef<HTMLDivElement | null>(null)
    const filterTriggerRef = useRef<HTMLButtonElement | null>(null)

    const [selectedOfficeId, setSelectedOfficeId] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [isOfficeOpen, setIsOfficeOpen] = useState(false)
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [activeTagIds, setActiveTagIds] = useState<Set<string>>(new Set())
    const [minCapacity, setMinCapacity] = useState<number | null>(null)

    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as Node

            if (officeSelectRef.current && !officeSelectRef.current.contains(target)) {
                setIsOfficeOpen(false)
            }

            if (
                filterPanelRef.current &&
                filterTriggerRef.current &&
                !filterPanelRef.current.contains(target) &&
                !filterTriggerRef.current.contains(target)
            ) {
                setIsFilterOpen(false)
            }
        }

        document.addEventListener('click', handleDocumentClick)
        return () => {
            document.removeEventListener('click', handleDocumentClick)
        }
    }, [])

    const toggleOfficeOpen = useCallback(() => {
        setIsOfficeOpen((value) => !value)
        setIsFilterOpen(false)
    }, [])

    const toggleFilterOpen = useCallback(() => {
        setIsFilterOpen((value) => !value)
        setIsOfficeOpen(false)
    }, [])

    const selectOffice = useCallback((officeId: string) => {
        setSelectedOfficeId(officeId)
        setIsOfficeOpen(false)
    }, [])

    const initializeSelectedOffice = useCallback((officeId: string) => {
        setSelectedOfficeId((current) => current || officeId)
    }, [])

    const toggleTag = useCallback((tagId: string) => {
        setActiveTagIds((previous) => toggleValueInSet(previous, tagId))
    }, [])

    const clearTags = useCallback(() => {
        setActiveTagIds(new Set())
    }, [])

    const retainValidTagIds = useCallback((validTagIds: Set<string>) => {
        setActiveTagIds((previous) => new Set(Array.from(previous).filter((tagId) => validTagIds.has(tagId))))
    }, [])

    return {
        officeSelectRef,
        filterPanelRef,
        filterTriggerRef,
        selectedOfficeId,
        searchTerm,
        isOfficeOpen,
        isFilterOpen,
        activeTagIds,
        minCapacity,
        setSearchTerm,
        setMinCapacity,
        toggleOfficeOpen,
        toggleFilterOpen,
        selectOffice,
        initializeSelectedOffice,
        toggleTag,
        clearTags,
        retainValidTagIds,
    }
}
